/** @format */

import {Fragment, ReactNode, useEffect, useMemo, useState} from 'react';

import {Box, Checkbox, Typography} from '@mui/material';

import {Trans} from 'react-i18next';
import {
  ExpandLess,
  ExpandMore,
  FormatListBulleted,
  Paid,
  Radar,
  ShowChart,
  ToggleOn,
} from '@mui/icons-material';

import {YalperPlatform} from 'graphql/generated/types';

import {classes} from './styles';
import {Group} from '../index';
import {EMPTY_PLATFORMS} from './SensorAdSystemIcons';
import {SelectableListItem} from './SelectableListItem';

const icons: Record<string, ReactNode> = {
  budget: <Paid className={classes.selectableListCategoryIcon} />,
  campaign_settings: <ToggleOn className={classes.selectableListCategoryIcon} />,
  ads_quality: <ShowChart className={classes.selectableListCategoryIcon} />,
  targeting_semantic_kernel: <Radar className={classes.selectableListCategoryIcon} />,
  feeds: <FormatListBulleted className={classes.selectableListCategoryIcon} />,
};

interface Sensor {
  group?: Group;
  id: string;
  humanName: string;
}

export interface Category {
  sensors: Sensor[];
  name: string;
  title: string;
  description: string;
}

interface SelectableListCategoryProps {
  selected: Set<string>;
  category: Category;
  groups: Group[];
  platformsByName: ReadonlyMap<string, ReadonlyArray<YalperPlatform>>;
  onUpdate: (item: string | Set<string>) => void;
}

const SelectableListCategory = ({
  selected,
  category,
  groups,
  platformsByName,
  onUpdate,
}: SelectableListCategoryProps) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const initialIsSelected = category.sensors.every(
    el => groups.find(group => group.sensors.includes(el.id)) || selected.has(el.id),
  );

  const [isSelected, setIsSelected] = useState(initialIsSelected);
  const isDisabled = useMemo(
    () => category.sensors.every(el => groups.find(group => group.sensors.includes(el.id))),
    [category.sensors, groups],
  );

  const isIndeterminate = useMemo(
    () => !isSelected && category.sensors.some(el => selected.has(el.id)),
    [category.sensors, isSelected, selected],
  );

  useEffect(() => {
    setIsSelected(
      category.sensors.every(
        el => groups.find(group => group.sensors.includes(el.id)) || selected.has(el.id),
      ),
    );
  }, [category.sensors, groups, selected]);

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSelected(!isSelected);
    const updated = new Set(selected);
    if (isSelected) {
      category.sensors.forEach(({id, group}) => !group && updated.delete(id));
    } else {
      category.sensors.forEach(({id, group}) => !group && updated.add(id));
    }
    onUpdate(updated);
  };

  const handleExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Fragment key={category.name}>
      <Box className={classes.selectableListCategory} onClick={handleExpandClick}>
        <Box onClick={handleCheckboxClick} sx={{display: 'flex', alignItems: 'center'}}>
          <Checkbox
            checked={isSelected}
            disabled={isDisabled}
            indeterminate={isIndeterminate}
            className={classes.selectableListCheckbox}
            color='default'
          />
          {icons[category.name]}
          <Typography>
            <Trans>{category.title}</Trans>
          </Typography>
        </Box>
        {isExpanded ? <ExpandLess /> : <ExpandMore />}
      </Box>
      {isExpanded &&
        category.sensors.map(({id, group}) => (
          <SelectableListItem
            key={`${category.name}_${id}`}
            id={id}
            platforms={platformsByName.get(id) ?? EMPTY_PLATFORMS}
            group={group}
            onSelect={onUpdate}
            isSelected={selected.has(id)}
          />
        ))}
    </Fragment>
  );
};

export {SelectableListCategory};
